import useUserAPIStore from 'apis/admin/user.api'
import { CheckboxArea } from 'components/area/Checkbox'
import { ChipArea } from 'components/area/Chip'
import { CheckBadge } from 'components/badge/CheckBadge'
import { Button } from 'components/button/Button/Button'
import { SelectBox } from 'components/input/SelectBox/SelectBox'
import Modal from 'components/modal/Modal'
import { TextFieldWithLock } from 'containers/Common/EditDetailsModal/EditDetailsGeneral/TextFieldWithLock'
import useGroupDropdown from 'hooks/Group/useGroupDropdown'
import { useAuth } from 'hooks/Auth/useAuth'
import useFocusTrapping from 'hooks/useFocusTrapping'
import useSearchFieldChange from 'hooks/useSearchFieldChange'
import { useEffect, useState } from 'react'
import useAccessStore from 'stores/useAccessStore'
import useModalStore from 'stores/useModalStore'
import useToastStore from 'stores/useToastStore'
import {
  UserCreateFormType,
  UserGetFormType,
  UserUpdateFormType,
  ValidateColorType,
  ValidUserCreateFormType,
} from 'types/admin/AdminType'
import { DropDownListOptionType } from 'types/common/CommonType'
import { Util } from 'utils'
import { getTranslation } from 'utils/translation'
import { StyledUserModalContainer } from './style'

/**
 * @description UserModal 한정
 * @author c_bae
 * @param isValuePresent 값의 유무를 체크하는 역할 (userForm.loginId 등).
 * @param isValid 유효성 검사 결과 (isValidUserForm.loginId.isValidCharacter 등).
 * @returns
 */
const getBadgeColor = (isValuePresent: any, isValid: boolean): ValidateColorType => {
  if (!isValuePresent) return 'gray' // 값이 없을 때 기본 색상
  return isValid ? 'blue' : 'red' // 값이 있으면 조건에 따라 색상 결정
}

interface UserModalProps {
  canModifyUsersRole?: boolean

  modalToggle: (state: boolean) => void
  dropdownOptions: {
    group: DropDownListOptionType[]
    role: DropDownListOptionType[]
  }
  userId?: UserGetFormType['userId']
}

export const UserModal = ({
  modalToggle,
  dropdownOptions,
  userId,
  canModifyUsersRole,
}: UserModalProps) => {
  const { user, logout } = useAuth()
  const { activeToast } = useToastStore()

  const { editMode, userModal, modalRef } = useModalStore()

  useFocusTrapping(userModal, modalRef)

  const { getUser, getUserList, createUser, getLoginIdCheckDuplicate, updateUser } =
    useUserAPIStore()

  const { searchSuggestions, handleSearchValue, handleSearchValueDatasourceChange } =
    useSearchFieldChange(dropdownOptions.group)

  const [isAble, setIsAble] = useState<boolean>(false)
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [selectedGroups, setSelectedGroups] = useState<DropDownListOptionType<string>[]>([])
  const [sortedDropdownOptionsRoles, setSortedDropdownOptionsRoles] = useState<
    DropDownListOptionType[]
  >(dropdownOptions.role)
  const [selectedRoles, setSelectedRoles] = useState<DropDownListOptionType[]>([])
  const [isSubmitted, setIsSubmitted] = useState(false)

  const [userForm, setUserForm] = useState<UserCreateFormType>({
    loginId: '',
    password: '',
    username: '',
    groups: [],
    mobile: '',
    email: '',
    roles: [],
  })

  const [userFormOriginal, setUserFormOriginal] = useState<UserUpdateFormType>({})
  const [isValidUserForm, setIsValidUserForm] = useState<ValidUserCreateFormType>({
    loginId: {
      isValidLength: false,
      isValidDuplicate: 'None',
      isValidCharacter: false,
    },
    password: {
      isValidLowerLetter: false,
      isValidNumber: false,
      isValidSpecialLetter: false,
      isValidLength: false,
    },
    passwordConfirm: {
      isValidEqualPassword: false,
    },
  })

  const isValidForCreated =
    isValidUserForm.loginId.isValidLength &&
    isValidUserForm.loginId.isValidDuplicate === true &&
    isValidUserForm.password.isValidLength &&
    isValidUserForm.password.isValidLowerLetter &&
    isValidUserForm.password.isValidNumber &&
    isValidUserForm.password.isValidSpecialLetter &&
    isValidUserForm.passwordConfirm.isValidEqualPassword

  const changedForm = Util.getDifferentOnSecondObjectAndNullAble(userFormOriginal, userForm)

  const selectedGroupLabels = new Set(selectedGroups.map((group) => group.label))

  const filteredSearchSuggestions = searchSuggestions.filter(
    (suggestion) => !selectedGroupLabels.has(suggestion)
  )

  const { getSelectedGroupInfo } = useGroupDropdown()

  const handleGroupSelect = (selectedGroupName: string) => {
    const selectedGroupInfo = getSelectedGroupInfo(
      selectedGroupName,
      dropdownOptions.group as DropDownListOptionType<string>[]
    )

    /** 선택한 그룹이 그룹목록에서 일치하지 않는 경우 */
    if (!selectedGroupInfo) {
      return
    }

    setSelectedGroups((prev) => [...prev, selectedGroupInfo])
    handleSearchValue('')
    handleSearchValueDatasourceChange('')
  }

  const handleSelectedGroupExclude = (excludeGroupName: string) => {
    setSelectedGroups((prev) => [...prev.filter((item) => item.label !== excludeGroupName)])
  }

  const handleSubmitButtonClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    setIsSubmitted(true)

    /** getDifferentOnSecondObjectAndNullAble 에서 group이랑 roles 는 변경되지 않았으면 포함시키지 않는 상황이라 수동으로 추가 */
    if (!changedForm.groups) {
      changedForm.groups = userFormOriginal.groups
    }

    if (!changedForm.roles) {
      changedForm.roles = userFormOriginal.roles
    }

    const res =
      editMode && userId
        ? await updateUser(
            {
              ...changedForm,
              username: !changedForm.username
                ? userForm.username
                : (changedForm.username as string),
            },
            userId
          )
        : await createUser(userForm)

    if (res.code === 200) {
      activeToast({
        message: editMode
          ? getTranslation('message', 'SUCCESS.UPDATE', ['USER_INFORMATION'])
          : getTranslation('message', 'SUCCESS.REGISTERED', ['USER_INFORMATION']),
        type: 'success',
        timeout: 3000,
      })
      modalToggle(false)
      getUserList()

      if (editMode && Object.keys(changedForm).includes('loginId') && user?.userId === userId) {
        activeToast({
          message: getTranslation('message', 'CONFIRM.USER_ID_CHANGED'),
          type: 'info',
          timeout: 3000,
        })

        setTimeout(logout, 3000)
      }

      return
    }
    activeToast({
      message: res?.message ?? getTranslation('message', 'ERROR.UPDATE', ['USER_INFORMATION']),
      type: 'error',
      timeout: 3000,
    })

    setIsSubmitted(false)
  }

  // groups & roles 제외
  const handleUserFormChange = (name: string, value: string) => {
    const updatedForm = { ...userForm, [name]: value }
    setUserForm(updatedForm)
  }

  const handlePasswordConfirmChange = (name: string, value: string) => {
    setPasswordConfirm(value)
  }

  /** 아이디 중복체크 */
  const handleIdCheckDuplicate = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (
      isValidUserForm.loginId.isValidLength &&
      isValidUserForm.loginId.isValidCharacter &&
      userForm.loginId &&
      userForm.loginId !== userFormOriginal.loginId
    ) {
      const res = await getLoginIdCheckDuplicate(userForm.loginId)

      setIsValidUserForm({
        ...isValidUserForm,
        loginId: {
          ...isValidUserForm.loginId,
          isValidDuplicate: !res,
        },
      })
    }
  }

  const { modifyRoles: modifyRolesRole } = useAccessStore((state) => state.accessRole.roles)

  useEffect(
    /** 아이디 유효성검사 및 중복체크*/
    function updateIsValidLoginId() {
      const checkValidLength = Util.checkValidateUser('loginId', userForm?.loginId, 'isValidLength')

      const checkValidCharacter = Util.checkValidateUser(
        'loginId',
        userForm?.loginId,
        'isValidCharacter'
      )

      setIsValidUserForm((prev) => ({
        ...prev,
        loginId: {
          isValidDuplicate: 'None',
          isValidLength: checkValidLength,
          isValidCharacter: checkValidCharacter,
        },
      }))
    },
    [userForm.loginId]
  )

  useEffect(
    /** 수정화면 데이터 세팅 */
    function fetchInitialUserDataInEditMode() {
      if (!(userId && editMode)) {
        return
      }

      const getUserInfoByUserId = async (id: string) => {
        const { loginId, username, groups, mobile, email, roles } = await getUser(id)

        const updatedForm = {
          loginId: loginId,
          password: '',
          username: username,
          groups: groups?.map((group) => group.groupId) ?? [],
          mobile: mobile ? Util.formatPhoneNumber(mobile) : '',
          email: email ?? '',
          roles: roles?.map((role) => role.rolesId) ?? [],
        }

        setUserForm(updatedForm)
        setUserFormOriginal(updatedForm) // 초기값이라 같은 값으로 설정

        if (groups?.length) {
          setSelectedGroups(
            groups.map((item) => {
              return {
                label: item.groupName,
                value: item.groupId,
              }
            })
          )
        }
        if (roles?.length) {
          const selectedRoleList = roles.map((role) => {
            return {
              label: role.rolesName,
              value: role.rolesId,
              /**
               * @description 현재 role이 sortedDropdownOptionsRoles에 없는 경우 부여 권한이 없는 것이므로 disabled 처리
               * @author jh_jeong
               */
              disabled: !sortedDropdownOptionsRoles.some((option) => option.value === role.rolesId),
            }
          })
          setSelectedRoles(selectedRoleList)
          // 선택된 role이 목록 제일 앞단에 위치하도록 정렬
          const sortedRoles = [
            ...selectedRoleList,
            ...sortedDropdownOptionsRoles.filter(
              (value) => !selectedRoleList.some((selected) => selected.value === value.value)
            ),
          ]
          setSortedDropdownOptionsRoles(sortedRoles)
        }

        setIsValidUserForm({
          ...isValidUserForm,
          loginId: {
            ...isValidUserForm.loginId,
            isValidDuplicate: true,
          },
        })
      }

      getUserInfoByUserId(userId)
    },
    [userId]
  )

  useEffect(
    /** 비밀번호 유효성검사 및 비밀번호 확인 체크 */
    function updateIsValidPassword() {
      if (!userForm.password) {
        return
      }

      const isValidObject = {
        isValidLowerLetter: Util.checkValidateUser(
          'password',
          userForm.password,
          'isValidLowerLetter'
        ),
        isValidLength: Util.checkValidateUser('password', userForm.password, 'isValidLength'),
        isValidSpecialLetter: Util.checkValidateUser(
          'password',
          userForm.password,
          'isValidSpecialLetter'
        ),
        isValidNumber: Util.checkValidateUser('password', userForm.password, 'isValidNumber'),
      }

      setIsValidUserForm((prev) => ({
        ...prev,
        password: isValidObject,
        passwordConfirm: {
          isValidEqualPassword: userForm.password === passwordConfirm,
        },
      }))
    },
    [userForm.password]
  )

  /** 비밀번호 확인 체크 */
  useEffect(
    function updateIsValidEqualPassword() {
      if (!passwordConfirm) {
        return
      }
      setIsValidUserForm({
        ...isValidUserForm,
        passwordConfirm: {
          isValidEqualPassword: userForm.password === passwordConfirm,
        },
      })
    },
    [passwordConfirm]
  )

  useEffect(
    /** selectedGroup 바뀌면 userForm에도 적용 */
    function updateGroupsInUserForm() {
      const updatedForm: UserCreateFormType = {
        ...userForm,
        groups: selectedGroups.map((item) => item.value),
      }
      setUserForm(updatedForm)
    },

    [selectedGroups.length]
  )

  useEffect(
    /** selectedRoles 바뀌면 userForm에도 적용 */
    function updateRolesInUserForm() {
      const updatedForm: UserCreateFormType = {
        ...userForm,
        roles: selectedRoles.map((role) => role.value as string),
      }
      setUserForm(updatedForm)
    },
    [selectedRoles]
  )

  const isClickableSubmitButton =
    isValidForCreated &&
    userForm.username.length > 0 &&
    selectedRoles.length > 0 &&
    selectedGroups.length > 0

  useEffect(
    /** submit button active 로직 : 등록 */
    function updateSubmitButtonActiveState() {
      if (!editMode) {
        setIsAble(isClickableSubmitButton)
      }
    },
    [isClickableSubmitButton]
  )

  /** submit button active 로직 : 수정 */
  useEffect(() => {
    if (!editMode) {
      return
    }

    const changedUserInfoKey = Object.keys(changedForm)

    if (!changedUserInfoKey.length) {
      setIsAble(false)
      return // 조건에 맞지 않으면 여기서 실행 중지
    }

    // 이하 코드는 조건에 맞을 때만 실행됨

    const isUsernameValid = () =>
      !changedUserInfoKey.includes('username') || Boolean(userForm.username)

    const isRolesValid = () => !changedUserInfoKey.includes('roles') || selectedRoles.length > 0

    const isGroupsValid = () => !changedUserInfoKey.includes('groups') || selectedGroups.length > 0

    const isLoginIdValid = () =>
      !changedUserInfoKey.includes('loginId') ||
      (userForm.loginId &&
        isValidUserForm.loginId.isValidLength &&
        isValidUserForm.loginId.isValidCharacter &&
        isValidUserForm.loginId.isValidDuplicate === true)

    const isPasswordValid = () =>
      !changedUserInfoKey.includes('password') ||
      (userForm.password &&
        isValidUserForm.password.isValidLength &&
        isValidUserForm.password.isValidLowerLetter &&
        isValidUserForm.password.isValidNumber &&
        isValidUserForm.password.isValidSpecialLetter &&
        isValidUserForm.passwordConfirm.isValidEqualPassword)

    const isRequiredFieldsValid = () =>
      userForm.loginId && userForm.username && userForm.roles?.length && userForm.groups?.length

    const updateIsAble =
      isUsernameValid() &&
      isRolesValid() &&
      isGroupsValid() &&
      isLoginIdValid() &&
      isPasswordValid() &&
      isRequiredFieldsValid()

    setIsAble(Boolean(updateIsAble))
  }, [
    changedForm,
    userForm.username,
    selectedRoles.length > 0,
    selectedGroups.length > 0,
    userForm.password,
    userForm.loginId,
  ])

  return (
    <Modal
      title={`${editMode ? getTranslation('resource', 'EDIT_USER') : getTranslation('resource', 'CREATE_USER')}${
        editMode && !canModifyUsersRole ? ` (${getTranslation('resource', 'READ_ONLY')})` : ''
      }`}
      modalToggle={modalToggle}
      fitSize={true}
      height="max-content"
      maxHeight="80%"
      actionButtons={
        editMode && !canModifyUsersRole
          ? []
          : [
              {
                size: 'large',
                width: '12rem',
                color: 'primary',
                type: 'solid',
                label: editMode
                  ? getTranslation('resource', 'SAVE')
                  : getTranslation('resource', 'CREATE'),
                disabled: !isAble || isSubmitted,
                onClick: handleSubmitButtonClick,
              },
            ]
      }
    >
      <StyledUserModalContainer>
        <div className="area">
          <div className="field-area">
            <div className="label-area">
              <div
                className={`label${
                  userForm.loginId &&
                  (!isValidUserForm.loginId.isValidLength ||
                    isValidUserForm.loginId.isValidDuplicate === false)
                    ? ` ${getTranslation('resource', 'ERROR')}`
                    : ''
                }`}
              >
                <p>
                  {getTranslation('resource', 'ID')}
                  <span>{' *'}</span>
                </p>
              </div>
              <Button
                size="small"
                color="strong"
                type="outline"
                disabled={
                  !isValidUserForm.loginId.isValidLength ||
                  !isValidUserForm.loginId.isValidCharacter
                }
                label={
                  isValidUserForm.loginId.isValidDuplicate !== 'None'
                    ? isValidUserForm.loginId.isValidDuplicate
                      ? getTranslation('resource', 'AVAILABLE_ID')
                      : getTranslation('resource', 'UNAVAILABLE_ID')
                    : getTranslation('resource', 'CHECK_ID_AVAILABILITY')
                }
                preIcon={!isValidUserForm.loginId.isValidDuplicate ? 'XCircle' : 'CheckCircle'}
                selected={isValidUserForm.loginId.isValidDuplicate === true}
                error={!isValidUserForm.loginId.isValidDuplicate}
                onClick={handleIdCheckDuplicate}
              />
            </div>
            <TextFieldWithLock
              readOnly={editMode && !canModifyUsersRole}
              widthValue="26rem"
              optionName="loginId"
              optionValue={userForm.loginId}
              optionPlaceholder={getTranslation('message', 'ACTION.ENTER', ['LOGIN_ID'])}
              handleOptionInfoChange={handleUserFormChange}
              error={
                userForm.loginId
                  ? !isValidUserForm.loginId.isValidLength ||
                    !isValidUserForm.loginId.isValidCharacter
                  : false
              }
              maxLength={70}
              lengthCount={false}
              validationOnTouch={true}
            />
            <div style={{ display: 'flex', gap: '0.8rem' }}>
              <CheckBadge
                textNoWrap
                title={getTranslation('resource', 'ID_MIN_LENGTH')}
                color={getBadgeColor(userForm.loginId, isValidUserForm.loginId.isValidLength)}
              />

              <CheckBadge
                textNoWrap
                title={getTranslation('resource', 'ID_FORBIDDEN_SLASH')}
                color={getBadgeColor(userForm.loginId, isValidUserForm.loginId.isValidCharacter)}
              />
            </div>
          </div>
        </div>
        <div className="area">
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <TextFieldWithLock
              type="password"
              readOnly={editMode && !canModifyUsersRole}
              required={!editMode}
              optionName="password"
              optionLabel={getTranslation('resource', 'PASSWORD')}
              optionValue={userForm.password}
              optionPlaceholder={getTranslation('message', 'ACTION.ENTER', ['PASSWORD'])}
              error={
                !isValidUserForm.password.isValidLowerLetter ||
                !isValidUserForm.password.isValidNumber ||
                !isValidUserForm.password.isValidSpecialLetter ||
                !isValidUserForm.password.isValidLength
              }
              handleOptionInfoChange={handleUserFormChange}
              maxLength={70}
              minLength={8}
              lengthCount={false}
              validationOnTouch={true}
            />

            <div style={{ display: 'flex', gap: '12px' }}>
              <CheckBadge
                textNoWrap
                title={getTranslation('resource', 'PW_REQ_LOWERCASE')}
                color={getBadgeColor(
                  userForm.password,
                  isValidUserForm.password.isValidLowerLetter
                )}
              />
              <CheckBadge
                textNoWrap
                title={getTranslation('resource', 'PW_REQ_NUMBER')}
                color={getBadgeColor(userForm.password, isValidUserForm.password.isValidNumber)}
              />
              <CheckBadge
                textNoWrap
                title={getTranslation('resource', 'PW_REQ_SPECIAL')}
                color={getBadgeColor(
                  userForm.password,
                  isValidUserForm.password.isValidSpecialLetter
                )}
              />
              <CheckBadge
                textNoWrap
                title={getTranslation('resource', 'PW_MIN_LENGTH')}
                color={getBadgeColor(userForm.password, isValidUserForm.password.isValidLength)}
              />
            </div>
          </div>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <TextFieldWithLock
              type={'password'}
              readOnly={editMode && !canModifyUsersRole}
              required={!editMode}
              optionName="password"
              optionLabel={getTranslation('resource', 'PW_CONFIRM')}
              optionValue={passwordConfirm}
              optionPlaceholder={getTranslation('message', 'ACTION.ENTER_AGAIN', ['PASSWORD'])}
              error={!isValidUserForm.passwordConfirm.isValidEqualPassword}
              handleOptionInfoChange={handlePasswordConfirmChange}
              maxLength={70}
              // minLength={8}
              lengthCount={false}
              validationOnTouch={true}
            />

            <div style={{ display: 'flex', gap: '12px' }}>
              <CheckBadge
                textNoWrap
                title={getTranslation('resource', 'PW_MATCH')}
                color={getBadgeColor(
                  passwordConfirm,
                  isValidUserForm.passwordConfirm.isValidEqualPassword
                )}
              />
            </div>
          </div>
        </div>
        <div className="area">
          <TextFieldWithLock
            widthValue="26rem"
            readOnly={editMode && !canModifyUsersRole}
            required
            optionName="username"
            optionLabel={getTranslation('resource', 'USER_NAME')}
            optionValue={userForm.username}
            optionPlaceholder={getTranslation('message', 'ACTION.ENTER', ['USER_NAME'])}
            handleOptionInfoChange={handleUserFormChange}
            maxLength={70}
            minLength={1}
          />
          {/* 수정모드인 경우 자신의 그룹정보만 조회 가능 */}
          {(!editMode || (editMode && userId === user?.userId)) && (
            <div className="group-area">
              {!editMode && (
                <SelectBox
                  size="large"
                  label={getTranslation('resource', 'GROUP')}
                  placeholder={getTranslation('message', 'ACTION.ADD', ['GROUP'])}
                  options={filteredSearchSuggestions.map((item) => {
                    return { label: item, value: item }
                  })}
                  handleValue={(selected) => {
                    handleGroupSelect(selected.value as string)
                  }}
                  required
                  comboBox
                />
              )}
              <ChipArea
                title={!editMode ? undefined : getTranslation('resource', 'GROUP')}
                selectedList={selectedGroups}
                onDelete={handleSelectedGroupExclude}
                hasClose={!editMode}
                height="7.2rem"
              />
            </div>
          )}
        </div>
        <div className="area row">
          <TextFieldWithLock
            readOnly={editMode && !canModifyUsersRole}
            optionName="mobile"
            optionLabel={getTranslation('resource', 'PHONE_NUMBER')}
            optionValue={userForm.mobile}
            optionPlaceholder="000 - 0000 - 0000"
            handleOptionInfoChange={handleUserFormChange}
            maxLength={70}
            minLength={0}
          />

          <TextFieldWithLock
            readOnly={editMode && !canModifyUsersRole}
            optionName="email"
            optionLabel={getTranslation('resource', 'EMAIL')}
            optionValue={userForm.email}
            optionPlaceholder="Email @ domain.kr"
            handleOptionInfoChange={handleUserFormChange}
            maxLength={70}
            minLength={0}
          />
        </div>

        {/* 자신의 정보인 경우 롤 조회만 가능 하도록 수정  */}
        <div className="area">
          {editMode && userId === user?.userId ? (
            <ChipArea
              title={getTranslation('resource', 'ROLES')}
              selectedList={selectedRoles as DropDownListOptionType<string>[]}
              onDelete={() => {}}
              hasClose={false}
              height="7.2rem"
            />
          ) : (
            <CheckboxArea
              disabled={(editMode && !canModifyUsersRole) || (editMode && !modifyRolesRole)}
              label={`${getTranslation('resource', 'ROLES')} ${
                editMode && !modifyRolesRole ? ` (${getTranslation('resource', 'READ_ONLY')})` : ''
              }`}
              required={true}
              options={sortedDropdownOptionsRoles}
              selectedOptions={selectedRoles}
              setSelectedOptions={setSelectedRoles}
            />
          )}
        </div>
      </StyledUserModalContainer>
    </Modal>
  )
}
